import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { Warning } from '../../components/LessonUI';

const HTMLIframe = () => {
    const iframeBasicCode = `<iframe src="https://example.com" 
        width="800" 
        height="600" 
        title="Example website">
</iframe>`;

    const youtubeEmbedCode = `<iframe width="560" height="315" 
        src="https://www.youtube.com/embed/VIDEO_ID" 
        title="YouTube video player" 
        frameborder="0" 
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
        allowfullscreen>
</iframe>`;

    const mapsEmbedCode = `<iframe 
    src="https://www.google.com/maps/embed?pb=..." 
    width="600" 
    height="450" 
    style="border:0;" 
    allowfullscreen="" 
    loading="lazy" 
    referrerpolicy="no-referrer-when-downgrade">
</iframe>`;

    const socialEmbedCode = `<!-- Twitter embed --&gt;
<iframe src="https://platform.twitter.com/embed/..." 
        width="550" 
        height="400">
</iframe>

<!-- Instagram embed --&gt;
<iframe src="https://www.instagram.com/p/.../embed" 
        width="400" 
        height="480" 
        frameborder="0">
</iframe>`;

    const deprecatedAttributesCode = `<!--  Deprecated attributes (use CSS instead) --&gt;
<iframe frameborder="0"    <!-- Use CSS: border: none --&gt;
        marginwidth="0"     <!-- Use CSS: padding --&gt;
        marginheight="0"    <!-- Use CSS: padding --&gt;
        scrolling="no">     <!-- Use CSS: overflow: hidden --&gt;
</iframe>`;

    const sandboxCode = `<!-- Fully sandboxed (most restrictive) --&gt;
<iframe src="untrusted.html" sandbox></iframe>

<!-- Allow specific features --&gt;
<iframe src="content.html" 
        sandbox="allow-scripts allow-same-origin allow-forms">
</iframe>`;

    const allowAttributeCode = `<iframe src="https://example.com" 
        allow="camera; microphone; geolocation; payment">
</iframe>

<!-- Deny all features except specified --&gt;
<iframe src="https://example.com" 
        allow="camera 'none'; microphone 'none'">
</iframe>`;

    const responsiveIframeCode = `<div class="iframe-container">
    <iframe src="https://www.youtube.com/embed/VIDEO_ID" 
            title="Video" 
            allowfullscreen>
    </iframe>
</div>

<style>
.iframe-container {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%; /* 16:9 aspect ratio (9/16 = 0.5625) */
    height: 0;
    overflow: hidden;
}

.iframe-container iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
}

/* 4:3 aspect ratio */
.aspect-4-3 {
    padding-bottom: 75%; /* 3/4 = 0.75 */
}

/* 1:1 aspect ratio (square) */
.aspect-1-1 {
    padding-bottom: 100%;
}
</style>`;

    const lazyLoadCode = `<!-- Defer loading until near viewport --&gt;
<iframe src="https://example.com" 
        loading="lazy" 
        title="Example">
</iframe>

<!-- Load immediately (default) --&gt;
<iframe src="https://example.com" 
        loading="eager" 
        title="Example">
</iframe>`;

    const postMessageParentCode = `<iframe id="myFrame" src="child.html"></iframe>

<script>
const iframe = document.getElementById('myFrame');

// Send message to iframe
iframe.contentWindow.postMessage('Hello from parent', '*');

// Receive messages from iframe
window.addEventListener('message', (event) => {
    // Verify origin for security!
    if (event.origin !== 'https://trusted-domain.com') return;
    
    console.log('Received:', event.data);
});
</script>`;

    const postMessageChildCode = `<script>
// Receive messages from parent
window.addEventListener('message', (event) => {
    // Verify origin!
    if (event.origin !== 'https://parent-domain.com') return;
    
    console.log('Received:', event.data);
    
    // Send response back
    event.source.postMessage('Hello from iframe', event.origin);
});

// Send message to parent
window.parent.postMessage('Message to parent', '*');
</script>`;

    const accessibleIframeCode = `<iframe src="content.html" 
        width="800" 
        height="600" 
        title="Descriptive title of iframe content" 
        loading="lazy">
    <!-- Fallback content for browsers without iframe support --&gt;
    <p>Your browser does not support iframes. 
       <a href="content.html">View content directly</a>.
    </p>
</iframe>`;

    const embedElementCode = `<!-- Embed PDF --&gt;
<embed src="document.pdf" 
       type="application/pdf" 
       width="800" 
       height="600">

<!-- Better: Use iframe for PDFs --&gt;
<iframe src="document.pdf" width="800" height="600"></iframe>

<!-- Or object with fallback --&gt;
<object data="document.pdf" type="application/pdf" width="800" height="600">
    <p>PDF cannot be displayed. <a href="document.pdf">Download PDF</a>.</p>
</object>`;

    const objectElementCode = `<!-- SVG with fallback --&gt;
<object data="image.svg" type="image/svg+xml" width="300" height="200">
    <img src="fallback.png" alt="Fallback image">
</object>

<!-- PDF with fallback --&gt;
<object data="document.pdf" type="application/pdf" width="800" height="600">
    <p>PDF viewer not available. 
       <a href="document.pdf">Download PDF</a>
    </p>
</object>`;

    const xframeErrorCode = `<!-- Error: Some sites prevent embedding --&gt;
<iframe src="https://example.com"></iframe>
<!-- Error: Refused to display in a frame because it set 'X-Frame-Options' to 'deny'. --&gt;`;

    const mixedContentCode = `<!-- HTTPS page embedding HTTP iframe --&gt;
<iframe src="http://example.com"></iframe>
<!-- Blocked: Mixed content --&gt;`;

    const youtubeFacadeCode = `<div class="youtube-facade" data-video-id="VIDEO_ID">
    <img src="https://i.ytimg.com/vi/VIDEO_ID/hqdefault.jpg" alt="Video thumbnail">
    <button class="play-button">Play</button>
</div>

<script>
document.querySelectorAll('.youtube-facade').forEach(el => {
    el.addEventListener('click', () => {
        const videoId = el.dataset.videoId;
        const iframe = document.createElement('iframe');
        iframe.src = \`https://www.youtube.com/embed/\${videoId}?autoplay=1\`;
        iframe.allow = 'autoplay; encrypted-media';
        iframe.allowFullscreen = true;
        el.replaceWith(iframe);
    });
});
</script>`;

    const secureEmbedCode = `<div class="video-container">
    <iframe 
        src="https://www.youtube.com/embed/VIDEO_ID" 
        title="Tutorial video about HTML iframes" 
        width="560" 
        height="315" 
        frameborder="0" 
        loading="lazy"
        sandbox="allow-scripts allow-same-origin allow-presentation"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
        allowfullscreen
        referrerpolicy="no-referrer-when-downgrade">
    </iframe>
</div>

<style>
.video-container {
    position: relative;
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
    padding-bottom: 56.25%;
    height: 0;
}

.video-container iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
}
</style>`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Audio & Video</div>
                <h1>Embedding Content (iframe, embed, object)</h1>
                <p className="hero-lead">
                    Embed external content, videos, maps, and third-party widgets. Learn iframe security,
                    sandbox attributes, and best practices for safe content embedding.
                </p>
            </div>

            <div className="content-section">
                <h2>The &lt;iframe&gt; Element</h2>
                <p>
                    <code>&lt;iframe&gt;</code> (inline frame) embeds another HTML page within the current page.
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={iframeBasicCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Common Use Cases</h2>

                <h3>1. Embedding YouTube Videos</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={youtubeEmbedCode} />
                </div>

                <h3>2. Embedding Google Maps</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={mapsEmbedCode} />
                </div>

                <h3>3. Embedding Social Media</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={socialEmbedCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Iframe Attributes</h2>

                <h3>Essential Attributes</h3>
                <div className="attributes-list">
                    <ul>
                        <li><code>src</code> - URL of the embedded page</li>
                        <li><code>width</code>, <code>height</code> - Dimensions (pixels or %)</li>
                        <li><code>title</code> - Accessibility label (required!)</li>
                        <li><code>name</code> - Name for targeting with links</li>
                        <li><code>loading</code> - lazy, eager (loading strategy)</li>
                    </ul>
                </div>

                <h3>Legacy Attributes (Avoid)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={deprecatedAttributesCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Security: The sandbox Attribute</h2>
                <p>
                    The <code>sandbox</code> attribute restricts what the embedded content can do.
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={sandboxCode} />
                </div>

                <h3>Sandbox Permissions</h3>
                <div className="sandbox-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Permission</th>
                                <th>Allows</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>allow-forms</code></td>
                                <td>Form submission</td>
                            </tr>
                            <tr>
                                <td><code>allow-scripts</code></td>
                                <td>JavaScript execution</td>
                            </tr>
                            <tr>
                                <td><code>allow-same-origin</code></td>
                                <td>Access to same origin (cookies, storage)</td>
                            </tr>
                            <tr>
                                <td><code>allow-popups</code></td>
                                <td>window.open(), target="_blank"</td>
                            </tr>
                            <tr>
                                <td><code>allow-top-navigation</code></td>
                                <td>Navigate top-level window</td>
                            </tr>
                            <tr>
                                <td><code>allow-modals</code></td>
                                <td>alert(), confirm(), prompt()</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <Warning><strong> Security Warning:</strong> Never use <code>allow-scripts</code> and
                    <code>allow-same-origin</code> together for untrusted content - the iframe can remove
                    its own sandbox!</Warning>
            </div>

            <div className="content-section">
                <h2>The allow Attribute (Feature Policy)</h2>
                <p>
                    Controls what browser features the embedded content can access.
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={allowAttributeCode} />
                </div>

                <h3>Common Features</h3>
                <ul>
                    <li><code>camera</code> - Access camera</li>
                    <li><code>microphone</code> - Access microphone</li>
                    <li><code>geolocation</code> - Get user location</li>
                    <li><code>payment</code> - Payment Request API</li>
                    <li><code>autoplay</code> - Autoplay media</li>
                    <li><code>fullscreen</code> - Fullscreen API</li>
                    <li><code>encrypted-media</code> - DRM protected content</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Responsive Iframes</h2>
                <p>
                    Make iframes scale with their container (16:9 aspect ratio):
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={responsiveIframeCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Lazy Loading Iframes</h2>
                <div className="code-example">
                    <CodeBlock language="html" code={lazyLoadCode} />
                </div>

                <p><strong>Benefits:</strong> Faster initial page load, reduced bandwidth, better performance.</p>
            </div>

            <div className="content-section">
                <h2>Communication Between Pages</h2>

                <h3>postMessage API</h3>
                <div className="code-example">
                    <div className="code-header">
                        <span>Parent Page</span>
                    </div>
                    <CodeBlock language="html" code={postMessageParentCode} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Child Page (inside iframe)</span>
                    </div>
                    <CodeBlock language="html" code={postMessageChildCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>SEO & Accessibility</h2>

                <h3>Best Practices</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={accessibleIframeCode} />
                </div>

                <h3>SEO Considerations</h3>
                <ul>
                    <li>Search engines may not index iframe content</li>
                    <li>Use iframes sparingly for important content</li>
                    <li>Provide alternative text/links</li>
                    <li>Ensure iframe content is also accessible directly</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>The &lt;embed&gt; Element</h2>
                <p>
                    <code>&lt;embed&gt;</code> embeds external content (PDFs, Flash, plugins).
                    Mostly legacy now.
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={embedElementCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>The &lt;object&gt; Element</h2>
                <p>
                    <code>&lt;object&gt;</code> embeds external resources with fallback support.
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={objectElementCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Security Best Practices</h2>
                <div className="security-checklist">
                    <h3>Do:</h3>
                    <ul>
                        <li>Always include <code>title</code> attribute</li>
                        <li>Use <code>sandbox</code> for untrusted content</li>
                        <li>Verify origins in postMessage handlers</li>
                        <li>Use HTTPS for iframe sources</li>
                        <li>Set appropriate CSP headers</li>
                        <li>Use <code>loading="lazy"</code> for below-the-fold iframes</li>
                        <li>Limit permissions with <code>allow</code> attribute</li>
                    </ul>

                    <h3> Don't:</h3>
                    <ul>
                        <li>Embed untrusted content without sandbox</li>
                        <li>Use both allow-scripts and allow-same-origin on untrusted content</li>
                        <li>Trust postMessage data without origin verification</li>
                        <li>Embed sensitive information in public iframes</li>
                        <li>Forget accessibility (title attribute)</li>
                        <li>Use iframes for primary content navigation</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Common Issues & Solutions</h2>

                <h3>Issue: "X-Frame-Options" Blocks Embedding</h3>
                <div className="code-example error">
                    <CodeBlock language="html" code={xframeErrorCode} />
                </div>
                <p><strong>Solution:</strong> You cannot embed sites that set X-Frame-Options. Use their official embed code or API.</p>

                <h3>Issue: Mixed Content</h3>
                <div className="code-example error">
                    <CodeBlock language="html" code={mixedContentCode} />
                </div>
                <p><strong>Solution:</strong> Use HTTPS for iframe source, or load page over HTTP (not recommended).</p>

                <h3>Issue: Iframe Not Responsive</h3>
                <p><strong>Solution:</strong> Use aspect ratio container trick (shown earlier).</p>
            </div>

            <div className="content-section">
                <h2>Performance Optimization</h2>
                <ul>
                    <li>Use <code>loading="lazy"</code> for below-the-fold iframes</li>
                    <li>Defer iframe loading until needed (add src with JavaScript)</li>
                    <li>Minimize number of iframes (each is a full page load)</li>
                    <li>Consider thumbnail/placeholder with click-to-load</li>
                    <li>Use facades for heavy embeds (YouTube, etc.)</li>
                </ul>

                <h3>YouTube Lite Embed (Facade Pattern)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={youtubeFacadeCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Complete Example: Secure Embed</h2>
                <div className="code-example">
                    <CodeBlock language="html" code={secureEmbedCode} />
                </div>
            </div>

        </div>
    );
};

export default HTMLIframe;


